import _ from "lodash";
import React from "react";
import { useLazyGetPendingBusinessSheetsQuery } from "../../../services/recommendations/businessSheetEndpoints";

export const PendingBusinessSheetContext = React.createContext();

export const usePendingBusinessSheetData = () => React.useContext(PendingBusinessSheetContext);

export const PendingBusinessSheetProvider = ({ children, filter = null, isReady = false }) => {
    const [pageLimit, setPageLimit] = React.useState(25);
    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());
    const [isLoadingState, setIsLoadingState] = React.useState(false);
    const [isClearing, setIsClearing] = React.useState(false);
    const [load, { data, error, isLoading, isFetching }] = useLazyGetPendingBusinessSheetsQuery();
    
    // extract the results
    const { totalPending, results: { pagination, results } } = data || { totalPending: 0, results: { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 } };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isNextPageLoading = isFetching === true || isLoading === true || isLoadingState === true;

    const clear = () => {
        if (isNextPageLoading || !isReady || !filter || _.isEmpty(filter)) {
            return;
        }

        const newCacheKey = new Date().valueOf();
        setIsLoadingState(_ => true);
        setIsClearing(_ => true);
        load({
            ...filter,
            cacheKey: newCacheKey,
            page: 1,
            limit: pageLimit,
        })
        .unwrap()
        .then(() => {
            setCacheKey(_prev => newCacheKey);
        })
        .finally(_ => {
            setIsLoadingState(_prev => false);
            setIsClearing(_prev => false);
        });
    };

    const loadMore = () => {
        // safety check to make sure we don't reload when already loading!
        if (isNextPageLoading || !hasMore || !isReady) {
            return;
        }

        // load the next page of results
        setIsLoadingState(_ => true);
        load({
            ...filter,
            cacheKey,
            page: page + 1,
            limit: pageLimit,
        }).unwrap().finally(_ => {
            setIsLoadingState(_prev => false);
        });
    }

    const isRowLoaded = (index) => {
        return !hasMore || index < results.length;
    }

    React.useEffect(clear, [ filter, isReady ]);

    return (
        <PendingBusinessSheetContext.Provider value={{
            data: results,
            hasMore,
            isFetching,
            error,
            isLoading,
            isNextPageLoading,
            isRowLoaded,
            loadMore,
            page,
            pagination,
            totalCount,
            totalPages,
            totalPending,
            isClearing
        }}>
            {children}
        </PendingBusinessSheetContext.Provider>
    )
}